import type {TRuleset} from '@kapitalist/contract/ruleset'
import type {TPlayerId} from '@kapitalist/contract/types'
import * as L from '@kapitalist/utils/List'
import {create} from '@kapitalist/utils/List'
import type {TSkin} from '~/game/skins'
import type {TMinorActionPromptDataBase, TMinorActionPromptType} from '~/game/types'
import {DataBackedModel} from '~/models/base'
import {JustSayNoCard} from '~/models/game/Cards/ActionCard'
import type GamePlayer from '~/models/game/GamePlayer'
import type GamePosition from '~/models/game/GamePosition'
import type ExposedHand from '~/models/game/Hands/ExposedHand'
import type InProgressMajorAction from '~/models/game/InProgressMajorAction'
import MinorActionJustSayNo from '~/models/game/MinorActions/MinorActionJustSayNo'
import type Portfolio from '~/models/game/Portfolio'
import type {AnyMinorAction} from '~/models/game/types'
import type {Tense} from '~/utils/language'

export type TMinorActionPromptValidResponseData = {
  gamePosition: GamePosition
  // the portfolio of the player making the decision. I avoided using `GamePosition.currentPlayer.portfolio`
  // here instead because spectators might be shown something about available moves later? not sure.
  portfolio: Portfolio
  inProgressMajorAction: InProgressMajorAction
  hand: ExposedHand
  rules: TRuleset
}

export type TMinorActionPromptUserFacingDescriptionData = {
  gamePosition: GamePosition

  // In order for there to be a MinorActionPrompt, there must also be these things in GamePosition, but there's no type
  // safety there, so we pass them separately
  // TODO could potentially make a GamePosition subclass that guarantees this and things like minorActor being present
  inProgressMajorAction: InProgressMajorAction

  tense: Tense
  skin: TSkin
}

/** This describes what happened to cause a minor action to be requested from a player.
 *  The backend sends this to the frontend.
 */
export default abstract class MinorActionPrompt<
  DataType extends TMinorActionPromptDataBase,
>
  extends DataBackedModel<DataType>
  implements TMinorActionPromptDataBase
{
  type: TMinorActionPromptType
  minorActorPlayerId: TPlayerId
  minorActor: GamePlayer // who is being prompted to act (often, the 'defender')
  majorActor: GamePlayer // who started the InProgressMajorAction (often, the 'attacker')

  constructor(data: DataType, majorActor: GamePlayer, minorActor: GamePlayer) {
    super(data)

    // workaround here necessary maybe until `declare` works
    // right now the properties above w/o initializers cause the `DataBackedModel` constructor's
    // dynamically assigned values to be overwritten by `undefined`
    this.type = data.type
    this.minorActorPlayerId = data.minorActorPlayerId
    this.majorActor = majorActor
    this.minorActor = minorActor
  }

  public validResponses(
    data: TMinorActionPromptValidResponseData
  ): AnyMinorAction[] {
    return L.create(this._validResponses(data))
  }

  protected abstract _validResponses(
    data: TMinorActionPromptValidResponseData
  ): L.ListCreationParameter<AnyMinorAction>

  // utility to add a JSN to a list of minor actions, iff applicable, since all
  // minor action prompts' validResponses will want that added on
  protected withJustSayNo(
    data: TMinorActionPromptValidResponseData,
    responses: L.ListCreationParameter<AnyMinorAction>
  ): AnyMinorAction[] {
    const respArr = create(responses)
    if (data.hand.cards.find(c => c instanceof JustSayNoCard)) {
      respArr.push(
        new MinorActionJustSayNo({
          minorActorPlayerId: this.minorActorPlayerId,
          type: 'justSayNo',
        })
      )
    }
    return respArr
  }

  public abstract userFacingDescription(
    data: TMinorActionPromptUserFacingDescriptionData
  ): string
}
